import { useQuery } from "@apollo/client";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { IconButton, LinearProgress } from "@mui/material";
import { DateTime } from "luxon";
import { Link, useSearchParams } from "react-router-dom";
import { gql } from "../../__generated__";
import styles from "./index.module.scss";

const PAGE_SIZE = 6;

export default function News() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = getPage(searchParams.get("page"));
  const { data, loading } = useQuery(GET_NEWS, {
    variables: { offset: page * PAGE_SIZE, limit: PAGE_SIZE },
  });
  const records = data?.news.records ?? [];
  const totalPages = Math.ceil((data?.news.totalRecords ?? 0) / PAGE_SIZE);

  if (loading) return <LinearProgress color="error" />;

  return (
    <main className={styles.page}>
      <section className={styles.grid} aria-label="News articles">
        {records.map((article) => (
          <article className={styles.card} key={article.id}>
            <img
              className={styles.image}
              src={article.imageUrl}
              alt=""
              loading="lazy"
            />
            <div className={styles.content}>
              <time className={styles.date} dateTime={article.date}>
                {formatDate(article.date)}
              </time>
              <h2 className={styles.title}>
                {article.translation?.title ?? ""}
              </h2>
              <p className={styles.description}>
                {getExcerpt(article.translation?.content ?? "")}
              </p>
              <Link className={styles.readMore} to={`/news/${article.id}`}>
                Read more <ArrowForwardIcon aria-hidden="true" />
              </Link>
            </div>
          </article>
        ))}
      </section>

      {totalPages > 1 && (
        <nav className={styles.pagination} aria-label="News pagination">
          <IconButton
            className={styles.pageArrow}
            aria-label="Previous page"
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
          >
            <ArrowBackIcon />
          </IconButton>
          {Array.from({ length: totalPages }, (_, index) => (
            <a
              key={index}
              className={`${styles.pageNumber} ${
                page === index ? styles.active : ""
              }`}
              href="#"
              onClick={(event) => {
                event.preventDefault();
                setPage(index);
              }}
            >
              {String(index + 1).padStart(2, "0")}
            </a>
          ))}
          <IconButton
            className={styles.pageArrow}
            aria-label="Next page"
            disabled={page >= totalPages - 1}
            onClick={() => setPage(page + 1)}
          >
            <ArrowForwardIcon />
          </IconButton>
        </nav>
      )}
    </main>
  );

  function setPage(nextPage: number) {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("page", String(nextPage + 1));
    setSearchParams(nextSearchParams);
  }
}

function getPage(value: string | null) {
  const page = Number(value);
  return Number.isInteger(page) && page > 0 ? page - 1 : 0;
}

function formatDate(date: string) {
  return DateTime.fromISO(date).toFormat("LLLL d, yyyy");
}

function getExcerpt(content: string) {
  return content.length > 180 ? `${content.slice(0, 177)}...` : content;
}

const GET_NEWS = gql(`
  query GetNews($offset: Int, $limit: Int) {
    news(input: { offset: $offset, limit: $limit }) {
      totalRecords
      records {
        id
        date
        imageUrl
        translation(lang: en) {
          title
          content
        }
      }
    }
  }
`);
